import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import type { components, paths } from '../schema';
import type {
  ErrorResult,
  OkResult,
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1, err, ok } from './utils';
import { TimelineEntry } from '../types/timeline';
import { FieldType } from '../types/field';

type ErrorsResponse = {
  errors: Record<string, string[]>;
  [k: string]: Record<string, string[]>;
};

type FilterConfig = {
  and: boolean;
  query: { and: boolean; filters: any[]; id: string }[];
};

export type SearchFieldsOptions = RequestOptionsWithParams<
  paths['/api/custom-objects/{object_identifier}/fields/search']['post']['parameters']['query']
> & {
  body?: paths['/api/custom-objects/{object_identifier}/fields/search']['post']['requestBody']['content']['application/json'];
};
export type SearchAllFieldsParams = RequestOptionsWithParams<
  paths['/api/custom-objects/settings-search']['get']['parameters']['query']
>;
export type GetCustomObjectsParams = Exclude<
  paths['/api/custom-objects']['get']['parameters']['query'],
  undefined
>;
export type GetAllCustomObjectsParams =
  paths['/api/custom-objects/settings-search']['get']['parameters']['query'];
export type FieldCategory =
  paths['/api/custom-objects/{object_pk}/categories']['get']['responses']['200']['content']['application/json'][number];
export interface FilterGroup
  extends Mutable<
    paths['/api/custom-objects/{object_pk}/filter-groups']['post']['responses']['201']['content']['application/json']
  > {
  config: FilterConfig;
  invalid?: boolean;
  deleted?: boolean;
}
export interface FilterGroupListItem
  extends Mutable<
    paths['/api/custom-objects/{object_pk}/filter-groups']['get']['responses']['200']['content']['application/json'][number]
  > {
  config: FilterConfig;
}
export type FilterGroupRequest = Partial<
  paths['/api/custom-objects/{object_pk}/filter-groups']['post']['requestBody']['content']['application/json']
>;
export type GetFilterGroupsParams = {
  type?: 'visible' | 'mine' | 'other';
};
export type CustomObject = Mutable<components['schemas']['CustomObject']>;
export type CustomObjectMinimal = Omit<
  components['schemas']['CustomObjectMinimalRead'],
  'fetch_url'
> & {
  fetch_url: 'client' | 'pipeline' | 'standard';
};
export type CustomObjectListItem =
  paths['/api/custom-objects']['get']['responses']['200']['content']['application/json']['results'][number];
export interface CustomObjectField
  extends Omit<
    Mutable<components['schemas']['CustomObjectDetailedFieldRead']>,
    'field_type' | 'properties' | 'relation'
  > {
  field_type: FieldType;
  properties: {
    allow_options: boolean;
    allow_multiple: boolean;
    allow_relations: boolean;
    allow_add: boolean;
    type: string;
    target_column: string;
  };
  relation: Required<
    components['schemas']['CustomObjectDetailedFieldRead']['relation']
  > & {
    id: string;
    name: string;
    description: string;
    entity_name: string;
    object_name: string;
    object_class: string;
    object_type: string;
    default_on_activities: boolean;
  };
}

export type CustomObjectDetail =
  paths['/api/custom-objects/{object_identifier}/detail']['get']['responses']['200']['content']['application/json'];

type SchemaCustomObjectRequest =
  paths['/api/custom-objects']['post']['requestBody']['content']['application/json'];
export interface CustomObjectRequest
  extends Omit<
    SchemaCustomObjectRequest,
    'default_color' | 'default_icon' | 'has_commerce_data'
  > {
  default_color?: SchemaCustomObjectRequest['default_color'];
  default_icon?: SchemaCustomObjectRequest['default_icon'];
  has_commerce_data?: SchemaCustomObjectRequest['has_commerce_data'];
}

export type StandardEntityRecord =
  components['schemas']['StandardEntityRecordRead'];

export type CustomObjectFieldRequest =
  paths['/api/custom-objects/{object_identifier}/fields']['post']['requestBody']['content']['application/json'];

export type Field =
  paths['/api/custom-objects/{object_identifier}/fields/{id}']['get']['responses']['200']['content']['application/json'];

export const createCustomObjectField = async (
  instance: AxiosInstance,
  customObjectId: string,
  data: CustomObjectFieldRequest,
  options: RequestOptions = {}
): Promise<Field> => {
  const res = await instance.post(
    `/custom-objects/${customObjectId}/fields`,
    data,
    options
  );

  return res.data;
};

export const createFilterGroup = async (
  instance: AxiosInstance,
  customObjectId: string,
  data: FilterGroupRequest,
  options?: RequestOptions
): Promise<OkResult<FilterGroup> | ErrorResult<ErrorsResponse>> => {
  try {
    const res = await instance.post<FilterGroup>(
      `/custom-objects/${customObjectId}/filter-groups`,
      data,
      options
    );

    return ok(res.data);
  } catch (e: any) {
    const error = e as AxiosError<ErrorsResponse>;

    return err(error.response?.data!, error.status!);
  }
};

export const deleteFilterGroup = async (
  instance: AxiosInstance,
  customObjectId: string,
  filterGroupId: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.delete(
    `/custom-objects/${customObjectId}/filter-groups/${filterGroupId}`,
    options
  );
};

export const updateFilterGroup = async (
  instance: AxiosInstance,
  customObjectId: string,
  filterGroupId: string,
  data: FilterGroupRequest,
  options: RequestOptions = {}
) => {
  const res = await instance.patch<FilterGroup>(
    `/custom-objects/${customObjectId}/filter-groups/${filterGroupId}`,
    data,
    options
  );

  return res.data;
};

export const getFilterGroups = async (
  instance: AxiosInstance,
  id: string,
  options: RequestOptionsWithParams<GetFilterGroupsParams> = {}
): Promise<FilterGroupListItem[]> => {
  const { params, ...opts } = options;
  const { type } = params ?? {};

  const url = type
    ? `/custom-objects/${id}/filter-groups/${type}`
    : `/custom-objects/${id}/filter-groups`;

  const res = await instance.get<FilterGroupListItem[]>(url, opts);

  return res.data;
};

export const getCustomObject = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<CustomObject> => {
  const res = await instance.get<CustomObject>(
    `/custom-objects/${id}`,
    options
  );

  return res.data;
};

export const createCustomObject = async (
  instance: AxiosInstance,
  data: CustomObjectRequest,
  options?: RequestOptions
): Promise<CustomObject> => {
  const res = await instance.post<CustomObject>(
    '/custom-objects',
    data,
    options
  );

  return res.data;
};

export const getCustomObjects = async (
  instance: AxiosInstance,
  options: RequestOptionsWithParams<GetCustomObjectsParams> = {}
): Promise<PaginatedResponse<CustomObjectListItem>> => {
  const res = await instance.get('/custom-objects', options);

  return res.data;
};

/**
 * @remarks this bypasses permissions to return all custom objects
 */
export const getAllCustomObjects = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<GetAllCustomObjectsParams>
): Promise<CustomObjectMinimal[]> => {
  const res = await instance.get<CustomObjectMinimal[]>(
    '/custom-objects/settings-search',
    options
  );

  return res.data;
};

export const getCustomObjectDetail = async (
  instance: AxiosInstance,
  customObjectId: string,
  options?: RequestOptions
): Promise<CustomObjectDetail> => {
  const res = await instance.get<CustomObjectDetail>(
    `/custom-objects/${customObjectId}/detail`,
    options
  );

  return res.data;
};

export const getCustomObjectCategories = async (
  instance: AxiosInstance,
  customObjectId: string,
  options?: RequestOptions
): Promise<FieldCategory[]> => {
  const res = await instance.get<FieldCategory[]>(
    `/custom-objects/${customObjectId}/categories`,
    options
  );

  return res.data;
};

export const getCustomObjectFields = async (
  instance: AxiosInstance,
  customObjectId: string,
  options: SearchFieldsOptions = {}
): Promise<CustomObjectField[]> => {
  const { body, params, ...opts } = options;

  const config: AxiosRequestConfig = { ...opts, params };

  const res = await instance.post<CustomObjectField[]>(
    `/custom-objects/${customObjectId}/fields/search`,
    body,
    config
  );

  return res.data;
};

/**
 * @remarks this bypasses permissions to return all custom object fields
 */
export const getAllCustomObjectFields = async (
  instance: AxiosInstance,
  customObjectId: string,
  options: RequestOptionsWithParams<SearchAllFieldsParams> = {}
): Promise<CustomObjectField[]> => {
  const res = await instance.get<CustomObjectField[]>(
    `/custom-objects/${customObjectId}/fields/settings-search`,
    options
  );

  return res.data;
};

export type CustomObjectTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'object_created'
  | 'object_updated'
  | 'object_deleted'
  | 'object_field_created'
  | 'object_field_updated'
  | 'object_field_deleted'
>;

export type CustomObjectHistoryParams = Required<
  paths['/api/custom-objects/{object_pk}/events-history']['get']['parameters']
>['query'];

export type CustomObjectHistory = Omit<
  paths['/api/custom-objects/{object_pk}/events-history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: CustomObjectTimelineEntry[];
};

export const getCustomObjectHistory = async (
  instance: AxiosInstance,
  id: string,
  options: RequestOptionsWithParams<CustomObjectHistoryParams> = {}
): Promise<CustomObjectHistory> => {
  const res = await instance.get<CustomObjectHistory>(
    `/custom-objects/${id}/events-history`,
    options
  );

  return res.data;
};

export class CustomObjectService {
  public createCustomObjectField;
  public createFilterGroup;
  public deleteFilterGroup;
  public getFilterGroups;
  public createCustomObject;
  public getAllCustomObjectFields;
  public getAllCustomObjects;
  public getCustomObjectDetail;
  public getCustomObjectCategories;
  public getCustomObjectFields;
  public getCustomObjectHistory;
  public getCustomObjects;

  constructor(private instance: AxiosInstance) {
    this.createCustomObjectField = curry1(createCustomObjectField)(
      this.instance
    );
    this.createFilterGroup = curry1(createFilterGroup)(this.instance);
    this.deleteFilterGroup = curry1(deleteFilterGroup)(this.instance);
    this.getFilterGroups = curry1(getFilterGroups)(this.instance);
    this.createCustomObject = curry1(createCustomObject)(this.instance);
    this.getAllCustomObjectFields = curry1(getAllCustomObjectFields)(
      this.instance
    );
    this.getAllCustomObjects = curry1(getAllCustomObjects)(this.instance);
    this.getCustomObjectDetail = curry1(getCustomObjectDetail)(this.instance);
    this.getCustomObjectCategories = curry1(getCustomObjectCategories)(
      this.instance
    );
    this.getCustomObjectFields = curry1(getCustomObjectFields)(this.instance);
    this.getCustomObjectHistory = curry1(getCustomObjectHistory)(this.instance);
    this.getCustomObjects = curry1(getCustomObjects)(this.instance);
  }
}
